import { useEventPanel } from '../contexts/EventPanelContext';
import { parseRRule } from '../lib/rrule';
import type { RecurrencePattern } from '../lib/rrule';

const DAY_NAMES = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
];
const POSITION_NAMES = ['1st', '2nd', '3rd', '4th'];

function parseLocalDatetime(value: string): Date {
  // The form stores local datetimes as YYYY-MM-DDTHH:MM without seconds.
  // Adding seconds makes the string a valid ISO 8601 local datetime and
  // ensures it parses correctly across browsers.
  console.log(
    '[EventView] parseLocalDatetime input:',
    JSON.stringify(value)
  );
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    const normalized = value + ':00';
    console.log(
      '[EventView] normalized to:',
      normalized,
      'Date:',
      new Date(normalized).toString()
    );
    return new Date(normalized);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const normalized = value + 'T00:00:00';
    console.log(
      '[EventView] normalized to:',
      normalized,
      'Date:',
      new Date(normalized).toString()
    );
    return new Date(normalized);
  }
  const d = new Date(value);
  console.log('[EventView] fallback Date:', d.toString());
  return d;
}

function formatDateRange(
  event: ReturnType<typeof useEventPanel>['event'],
  defaultValues: ReturnType<
    typeof useEventPanel
  >['defaultValues'],
  startAt: string,
  endAt: string,
  allDay: boolean
): string {
  console.log(
    '[EventView] formatDateRange startAt:',
    JSON.stringify(startAt),
    'endAt:',
    JSON.stringify(endAt),
    'allDay:',
    allDay
  );
  const start = parseLocalDatetime(startAt);
  const end = parseLocalDatetime(endAt);
  console.log(
    '[EventView] parsed start:',
    start.toString(),
    'end:',
    end.toString()
  );
  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime())
  ) {
    console.error('[EventView] invalid event date range', {
      event,
      defaultValues,
      startAt,
      endAt,
      allDay,
      parsedStart: start.toString(),
      parsedEnd: end.toString(),
    });
  }
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };

  if (allDay) {
    return `${start.toLocaleDateString('en-US', options)} — ${end.toLocaleDateString('en-US', options)}`;
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  };
  return `${start.toLocaleDateString('en-US', options)} · ${start.toLocaleTimeString('en-US', timeOptions)} — ${end.toLocaleTimeString('en-US', timeOptions)}`;
}

function formatRecurrencePills(
  pattern: RecurrencePattern
): { type: string; values: string[] } | null {
  if (pattern.type === 'none') return null;

  const typePill =
    pattern.type.charAt(0).toUpperCase() +
    pattern.type.slice(1);
  const values: string[] = [];

  switch (pattern.type) {
    case 'daily': {
      const unit = pattern.interval === 1 ? 'day' : 'days';
      values.push(`every ${pattern.interval} ${unit}`);
      break;
    }
    case 'weekly': {
      values.push(
        ...pattern.days.map(dayIndex => DAY_NAMES[dayIndex])
      );
      break;
    }
    case 'monthly': {
      const position =
        POSITION_NAMES[pattern.position - 1] ??
        `${pattern.position}th`;
      const dayName = DAY_NAMES[pattern.day];
      values.push(`${position} ${dayName}`);
      break;
    }
    case 'yearly': {
      const unit =
        pattern.interval === 1 ? 'year' : 'years';
      values.push(`every ${pattern.interval} ${unit}`);
      break;
    }
  }

  return { type: typePill, values };
}

function Pill({
  children,
  filled,
  accentColor,
}: {
  children: React.ReactNode;
  filled?: boolean;
  accentColor: string;
}) {
  return (
    <span
      className={[
        'inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-mono',
        filled ? 'text-white' : 'border text-ink-900',
      ].join(' ')}
      style={
        filled
          ? {
              backgroundColor: accentColor,
              borderColor: accentColor,
            }
          : { borderColor: accentColor }
      }
    >
      {children}
    </span>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
        {label}
      </span>
      <div className="font-serif text-sm text-ink-900 min-h-[1.25em]">
        {children}
      </div>
    </div>
  );
}

export default function EventView() {
  const {
    event,
    defaultValues,
    canEdit,
    accentColor,
    enterEditMode,
    onClose,
  } = useEventPanel();

  console.log(
    '[EventView] defaultValues:',
    JSON.stringify(defaultValues, null, 2)
  );

  const {
    title,
    description,
    startAt,
    endAt,
    allDay,
    recurrence,
    tags,
    address,
    phone,
  } = defaultValues;

  console.log('[EventView] destructured:', {
    title,
    startAt,
    endAt,
    allDay,
  });

  const recurrencePills = (() => {
    const pattern =
      recurrence ??
      (event?.rrule
        ? parseRRule(event.rrule)
        : { type: 'none' });
    return pattern ? formatRecurrencePills(pattern) : null;
  })();

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {canEdit && (
          <button
            type="button"
            onClick={enterEditMode}
            className="btn-primary w-full"
            style={{ backgroundColor: accentColor }}
          >
            Edit
          </button>
        )}

        <h2 className="font-serif text-2xl font-bold text-ink-900">
          {title || (
            <span className="text-ink-200">
              Untitled event
            </span>
          )}
        </h2>

        <div className="space-y-1">
          <p className="font-serif text-base text-ink-900">
            {formatDateRange(
              event,
              defaultValues,
              startAt,
              endAt,
              allDay
            )}
          </p>
          {allDay && (
            <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
              All-day event
            </span>
          )}
        </div>

        <FieldRow label="Description">
          {description || (
            <span className="text-ink-200">&nbsp;</span>
          )}
        </FieldRow>

        {recurrencePills && (
          <div className="space-y-1.5">
            <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
              Repeats
            </span>
            <div className="flex flex-wrap items-center gap-1.5">
              <Pill filled accentColor={accentColor}>
                {recurrencePills.type}
              </Pill>
              {recurrencePills.values.map((value, idx) => (
                <Pill
                  key={`${value}-${idx}`}
                  accentColor={accentColor}
                >
                  {value}
                </Pill>
              ))}
            </div>
          </div>
        )}

        <FieldRow label="Address">
          {address ? (
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(address.trim())}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-ink-900 hover:text-ink-700 transition-colors underline underline-offset-2"
            >
              {address}
            </a>
          ) : (
            <span className="text-ink-200">&nbsp;</span>
          )}
        </FieldRow>

        <FieldRow label="Contact #">
          {phone ? (
            <a
              href={`tel:${phone.replace(/\s/g, '')}`}
              className="text-ink-900 hover:text-ink-700 transition-colors underline underline-offset-2"
            >
              {phone}
            </a>
          ) : (
            <span className="text-ink-200">&nbsp;</span>
          )}
        </FieldRow>

        {tags.length > 0 && (
          <div className="space-y-1.5">
            <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
              Tags
            </span>
            <div className="flex flex-wrap gap-1.5">
              {tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-0.5 rounded-full bg-paper-200 font-mono text-xs text-ink-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="p-5 pt-2 space-y-2 shrink-0 border-t border-paper-200">
        <button
          type="button"
          onClick={onClose}
          className="btn-ghost w-full"
        >
          Close
        </button>
      </div>
    </div>
  );
}
